// 実行中タイマーの表示+操作(開始/一時停止/再開/完了)コンポーネント群。

import { useState, useEffect } from "react";
import { Stack, Typography, Button, Chip } from "@mui/material";
import { formatHMS } from "../../../util/dateFormat";
import { computeElapsedSec, TimerClientState } from "./helpers";

// 計測中の行が1つでもある間だけ 1 秒間隔で now を更新する。可視復帰/フォーカス時も即更新(バックグラウンド追従)。
export function useTimerNow(hasRunning: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!hasRunning) {
      return;
    }
    const refresh = () => setNow(Date.now());
    refresh();
    const timer = window.setInterval(refresh, 1000);
    const onVis = () => { if (document.visibilityState === "visible") { refresh(); } };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", refresh);
    };
  }, [hasRunning]);
  return now;
}

// 実行中タイマーの表示+操作(開始/一時停止/再開/完了)。HH:MM:SS を monospace で大きく表示。
export function TimerControl(props: {
  startedAt: number | null; finishedAt: number | null; durationSeconds: number | null;
  pause: TimerClientState | undefined; now: number; busy: boolean;
  onStart?: () => void; onPause: () => void; onResume: () => void; onFinish: () => void;
}) {
  const finished = props.finishedAt != null;
  const started = props.startedAt != null;
  const paused = !finished && started && props.pause != null && props.pause.pausedAt != null;
  const running = !finished && started && !paused;
  const elapsed = finished && props.durationSeconds != null
    ? props.durationSeconds
    : computeElapsedSec(props.startedAt, props.finishedAt, props.pause, props.now);
  const color = finished ? "text.secondary" : running ? "success.main" : "warning.main";
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography sx={{ fontFamily: "monospace", fontWeight: "bold", fontSize: "1.1rem", minWidth: 92, color: color }}>{formatHMS(elapsed)}</Typography>
      {!started && props.onStart != null && (
        <Button size="small" variant="contained" color="success" disabled={props.busy} onClick={props.onStart}>開始</Button>
      )}
      {running && <Button size="small" variant="outlined" disabled={props.busy} onClick={props.onPause}>一時停止</Button>}
      {paused && <Button size="small" variant="contained" color="warning" disabled={props.busy} onClick={props.onResume}>再開</Button>}
      {(running || paused) && <Button size="small" variant="contained" disabled={props.busy} onClick={props.onFinish}>完了</Button>}
      {finished && <Chip size="small" color="success" label="完了" sx={{ pointerEvents: "none" }} />}
    </Stack>
  );
}
